import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query'
import { reportsApi } from '@/lib/api'
import type { EsgReport, EsgReportFormData, EsgReportMetrics } from '@/types'

export const reportKeys = {
  all: ['reports'] as const,
  lists: () => [...reportKeys.all, 'list'] as const,
  list: (params?: { client_id?: number }) => [...reportKeys.lists(), params] as const,
  preview: (params: { client_id: number; date_from: string; date_to: string } | null) =>
    [...reportKeys.all, 'preview', params] as const,
}

export function useEsgReports(params?: {
  client_id?: number
}): UseQueryResult<EsgReport[]> {
  return useQuery({
    queryKey: reportKeys.list(params),
    queryFn: async () => {
      const res = await reportsApi.list(params)
      return res.data
    },
  })
}

export function useEsgPreview(
  params: { client_id: number; date_from: string; date_to: string } | null
): UseQueryResult<EsgReportMetrics> {
  return useQuery({
    queryKey: reportKeys.preview(params),
    queryFn: async () => {
      const res = await reportsApi.preview(params!)
      return res.data
    },
    enabled: !!params && !!params.client_id && !!params.date_from && !!params.date_to,
  })
}

export function useCreateEsgReport(): UseMutationResult<Blob, Error, EsgReportFormData> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: EsgReportFormData): Promise<Blob> => {
      const res = await reportsApi.generate(data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKeys.lists() })
    },
  })
}

export function useDownloadEsgReport(): UseMutationResult<Blob, Error, number> {
  return useMutation({
    mutationFn: async (id: number): Promise<Blob> => {
      const res = await reportsApi.download(id)
      return res.data
    },
  })
}
